import { useState } from 'react';
import { Button, Input, Spinner, Tooltip, makeStyles } from '@fluentui/react-components';
import { adminApplyUsage, type UsagePreset } from '../api/admin.js';
import { useAuthStore } from '../stores/authStore.js';
import { useUsageStore } from '../stores/usageStore.js';

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: 'var(--space-m)',
  },
  presets: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: 'var(--space-s)',
  },
  manualToggle: {
    background: 'transparent',
    border: 'none',
    color: 'var(--text-secondary)',
    cursor: 'pointer',
    fontSize: 'var(--fs-200)',
    padding: 0,
    textAlign: 'left',
    textDecoration: 'underline',
    width: 'fit-content',
  },
  manualGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr) auto',
    gap: 'var(--space-s)',
    alignItems: 'end',
  },
  field: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  fieldLabel: {
    fontFamily: 'var(--font-mono)',
    fontSize: 'var(--fs-100)',
    color: 'var(--text-tertiary)',
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
  },
  status: {
    fontSize: 'var(--fs-200)',
    fontFamily: 'var(--font-mono)',
    color: 'var(--text-secondary)',
  },
});

type PresetDef = { id: UsagePreset; label: string; desc: string };

const PRESETS: PresetDef[] = [
  { id: 'verde',    label: '🟢 Verde',         desc: 'Todos al 30 % del plan vigente' },
  { id: 'amarillo', label: '🟡 Amarillo',      desc: 'Todos al 80 % del plan vigente' },
  { id: 'rojo',     label: '🔴 Rojo',          desc: 'Todos al 95 % del plan vigente' },
  { id: 'tope',     label: '⛔ Tope (100 %)',  desc: 'Todos al límite exacto del plan vigente' },
  { id: 'overage',  label: '🧡 Overage',       desc: 'Todos al 110 % (excede el limit)' },
  { id: 'reset',    label: '↺ Reset',          desc: 'Plan Teams + counters a 0 (estado limpio)' },
];

/**
 * Panel de pruebas para el equipo de QA. Sólo visible para admin/owner
 * del tenant ECOSOFT (gate ya aplicado en el componente padre).
 *
 * Permite:
 *   - Aplicar 1 de 6 presets de un click (verde/amarillo/rojo/tope/overage/reset).
 *   - Fijar counters manualmente (chats=29 para probar "último permitido").
 *
 * Cada acción llama `POST /api/v1/admin/tenants/:id/usage` y refresca
 * el usage store para que la pill y los meters reflejen el cambio sin
 * recargar la página.
 */
export function AdminUsageSimulator({ tenantId, tenantName }: { tenantId: string; tenantName: string }) {
  const styles = useStyles();
  const refreshUsage = useUsageStore((s) => s.refresh);
  const [busy, setBusy] = useState<UsagePreset | 'manual' | null>(null);
  const [result, setResult] = useState<string | null>(null);
  const [showManual, setShowManual] = useState(false);
  const [m, setM] = useState({ projects: '', chats: '', concepts: '' });

  async function applyPreset(preset: UsagePreset) {
    setBusy(preset);
    setResult(null);
    try {
      const r = await adminApplyUsage(tenantId, { preset });
      setResult(
        `Aplicado "${preset}": plan=${r.plan}, proyectos=${r.counters.projects_created}, ` +
          `chats=${r.counters.chats_sent}, conceptos=${r.counters.concepts_quoted}`,
      );
      await refreshUsage();
    } catch (err) {
      setResult(`ERROR: ${(err as Error).message}`);
    } finally {
      setBusy(null);
    }
  }

  async function applyManual() {
    const counters: { projects_created?: number; chats_sent?: number; concepts_quoted?: number } = {};
    const p = m.projects.trim();
    const c = m.chats.trim();
    const k = m.concepts.trim();
    if (p !== '') counters.projects_created = Math.max(0, Number(p));
    if (c !== '') counters.chats_sent = Math.max(0, Number(c));
    if (k !== '') counters.concepts_quoted = Math.max(0, Number(k));
    if (Object.keys(counters).length === 0) {
      setResult('Indica al menos un counter.');
      return;
    }
    setBusy('manual');
    setResult(null);
    try {
      const r = await adminApplyUsage(tenantId, { counters });
      setResult(
        `Counters actualizados: proyectos=${r.counters.projects_created}, ` +
          `chats=${r.counters.chats_sent}, conceptos=${r.counters.concepts_quoted}`,
      );
      await refreshUsage();
    } catch (err) {
      setResult(`ERROR: ${(err as Error).message}`);
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className={styles.root}>
      <div className={styles.presets}>
        {PRESETS.map((p) => (
          <Tooltip key={p.id} content={p.desc} relationship="label">
            <Button
              appearance={p.id === 'reset' ? 'secondary' : 'outline'}
              disabled={busy !== null}
              icon={busy === p.id ? <Spinner size="extra-tiny" /> : undefined}
              onClick={() => void applyPreset(p.id)}
            >
              {p.label}
            </Button>
          </Tooltip>
        ))}
      </div>

      <button
        type="button"
        className={styles.manualToggle}
        onClick={() => setShowManual((v) => !v)}
      >
        {showManual ? '▲ Ocultar override manual' : '▼ Override manual (counters específicos)'}
      </button>

      {showManual && (
        <div className={styles.manualGrid}>
          <div className={styles.field}>
            <span className={styles.fieldLabel}>proyectos_creados</span>
            <Input
              type="number"
              min={0}
              value={m.projects}
              onChange={(_, d) => setM({ ...m, projects: d.value })}
              placeholder="—"
            />
          </div>
          <div className={styles.field}>
            <span className={styles.fieldLabel}>chats_enviados</span>
            <Input
              type="number"
              min={0}
              value={m.chats}
              onChange={(_, d) => setM({ ...m, chats: d.value })}
              placeholder="—"
            />
          </div>
          <div className={styles.field}>
            <span className={styles.fieldLabel}>conceptos_cotizados</span>
            <Input
              type="number"
              min={0}
              value={m.concepts}
              onChange={(_, d) => setM({ ...m, concepts: d.value })}
              placeholder="—"
            />
          </div>
          <Button
            appearance="primary"
            disabled={busy !== null}
            icon={busy === 'manual' ? <Spinner size="extra-tiny" /> : undefined}
            onClick={() => void applyManual()}
          >
            Aplicar
          </Button>
        </div>
      )}

      {result && (
        <span
          className={styles.status}
          style={{ color: result.startsWith('ERROR') ? 'var(--bad)' : 'var(--text-secondary)' }}
        >
          {result} · Tenant: {tenantName}
        </span>
      )}
    </div>
  );
}
